// src/pages/portal/PortalExpensesPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format, subDays } from 'date-fns';
import PortalLayout from './PortalLayout';
import { employeeService, expenseService, sessionService, inventoryService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import tenantStorage from '../../services/tenantStorage';
import DeletionConfirmModal from '../../components/common/DeletionConfirmModal';
import useDeletionConfirm from '../../hooks/useDeletionConfirm';
import useIsMobile from '../../hooks/useIsMobile';
import MobileCard from '../../components/common/MobileCard';
import MobileHero from '../../components/common/MobileHero';
import MobileBanner from '../../components/common/MobileBanner';
import { C } from '../../styles/theme';

// ─── Brand tokens ─────────────────────────────────────────────────────────────

const FONT = "'Plus Jakarta Sans', sans-serif";

// ─── Default categories (overridden by tenant config) ─────────────────────────
const DEFAULT_DAILY_CATEGORIES    = ['Inventory', 'Equipment', 'Maintenance', 'Other'];
const DEFAULT_OVERHEAD_CATEGORIES = ['Rent', 'Utilities', 'Bills', 'Inventory', 'Inventory Purchase', 'Other'];

// ─── Category badge colours ───────────────────────────────────────────────────
const CAT_COLOURS = {
    Inventory:   { color: C.green,  bg: C.greenLight  },
    Equipment:   { color: C.amber,  bg: C.amberLight  },
    Maintenance: { color: C.blue,   bg: C.blueLight   },
    Rent:        { color: C.purple, bg: C.purpleLight  },
    Utilities:   { color: C.amber,  bg: C.amberLight  },
    Bills:       { color: C.blue,   bg: C.blueLight   },
    Other:       { color: C.muted,  bg: '#f1f5f9'     },
};
const catColour = (cat) => CAT_COLOURS[cat] || { color: C.muted, bg: '#f1f5f9' };

// ─── Shared primitives ────────────────────────────────────────────────────────
const Badge = ({ children, color, bg }) => (
    <span style={{
        fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
        borderRadius: 5, padding: '2px 8px',
        background: bg, color, display: 'inline-block',
        fontFamily: FONT,
    }}>
        {children}
    </span>
);

// ─── Toast ────────────────────────────────────────────────────────────────────
const ToastContainer = ({ toasts }) => (
    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
        {toasts.map(t => (
            <div key={t.id} style={{
                padding: '10px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600,
                background: t.type === 'success' ? '#eff5ff' : C.redLight,
                border: `1.5px solid ${t.type === 'success' ? '#86efac' : '#fca5a5'}`,
                color: t.type === 'success' ? '#16a34a' : C.red,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                fontFamily: FONT, pointerEvents: 'auto', minWidth: 220,
                animation: 'fadeIn 0.2s ease',
            }}>
                {t.message}
            </div>
        ))}
    </div>
);

// ─── Modal wrapper ────────────────────────────────────────────────────────────
const Modal = ({ open, onClose, title, children, width = 520 }) => {
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open) return null;
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 12, width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', fontFamily: FONT }}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{title}</div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: C.mutedLight, cursor: 'pointer', lineHeight: 1, padding: 0 }} aria-label="Close">✕</button>
                </div>
                <div style={{ padding: '20px' }}>{children}</div>
            </div>
        </div>
    );
};

// ─── Form field primitives ────────────────────────────────────────────────────
const Label = ({ children }) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 5, fontFamily: FONT }}>
        {children}
    </div>
);

const Input = ({ style, ...props }) => (
    <input style={{
        width: '100%', border: `1px solid ${C.border}`, borderRadius: 7,
        padding: '8px 11px', fontSize: 13, color: C.text, fontFamily: FONT,
        outline: 'none', boxSizing: 'border-box', background: C.white,
        ...style,
    }} {...props} />
);

const Select = ({ style, children, ...props }) => (
    <select style={{
        width: '100%', border: `1px solid ${C.border}`, borderRadius: 7,
        padding: '8px 11px', fontSize: 13, color: C.text, fontFamily: FONT,
        outline: 'none', boxSizing: 'border-box', background: C.white,
        ...style,
    }} {...props}>{children}</select>
);

const Textarea = ({ style, ...props }) => (
    <textarea style={{
        width: '100%', border: `1px solid ${C.border}`, borderRadius: 7,
        padding: '8px 11px', fontSize: 13, color: C.text, fontFamily: FONT,
        outline: 'none', boxSizing: 'border-box', resize: 'vertical', background: C.white,
        ...style,
    }} {...props} />
);

const Btn = ({ children, variant = 'primary', style, ...props }) => {
    const base = {
        border: 'none', borderRadius: 7, padding: '8px 16px',
        fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT,
        transition: 'background 0.15s',
    };
    const variants = {
        primary:   { background: C.green, color: C.white },
        secondary: { background: C.white, color: C.muted, border: `1px solid ${C.border}` },
        danger:    { background: C.redLight, color: C.red },
    };
    return <button style={{ ...base, ...variants[variant], ...style }} {...props}>{children}</button>;
};

// ─── Summary card ─────────────────────────────────────────────────────────────
const SummaryCard = ({ label, value, highlight }) => (
    <div style={{
        borderRadius: 9, padding: '10px 14px',
        background: highlight ? C.greenLight : C.bg,
        border: `1px solid ${highlight ? '#b7dfc8' : C.border}`,
    }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: highlight ? C.green : C.muted, textTransform: 'uppercase', letterSpacing: 0.8, fontFamily: FONT }}>{label}</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: highlight ? C.green : C.text, marginTop: 3, fontFamily: FONT }}>{value}</div>
    </div>
);

// ─── Table ────────────────────────────────────────────────────────────────────
const TableHeader = ({ cols }) => (
    <div style={{ display: 'grid', gridTemplateColumns: cols.map(c => c.width || '1fr').join(' '), background: C.bg, padding: '8px 16px', gap: 12, borderBottom: `1px solid ${C.border}` }}>
        {cols.map(c => (
            <div key={c.key} style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, fontFamily: FONT }}>{c.label}</div>
        ))}
    </div>
);

const TableRow = ({ cols, row, onClick }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'grid', gridTemplateColumns: cols.map(c => c.width || '1fr').join(' '),
                padding: '10px 16px', gap: 12, alignItems: 'center',
                borderBottom: `1px solid ${C.bg}`,
                background: hovered ? C.greenLight : C.white,
                cursor: onClick ? 'pointer' : 'default',
                transition: 'background 0.12s',
            }}>
            {cols.map(c => (
                <div key={c.key}>{c.render ? c.render(row) : <span style={{ fontSize: 13, color: C.text, fontFamily: FONT }}>{row[c.key]}</span>}</div>
            ))}
        </div>
    );
};

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyState = ({ icon, message, sub }) => (
    <div style={{ padding: '48px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 6, fontFamily: FONT }}>{message}</div>
        {sub && <div style={{ fontSize: 12, color: C.muted, fontFamily: FONT }}>{sub}</div>}
    </div>
);

// ─── Mobile expense card ──────────────────────────────────────────────────────
const fmtMoney = (n, currency) => `${currency}${(parseFloat(n) || 0).toFixed(2)}`;

const MobileExpenseRow = ({ expense, currency, isOverhead, onEdit, onDelete, t }) => {
    const dateLine = isOverhead ? expense.date : (expense.time ? expense.time.slice(0, 5) : '');
    const subtitle = [dateLine, expense.category].filter(Boolean).join(' · ');
    return (
        <MobileCard
            avatar={{ initials: (expense.category || '?').slice(0, 2).toUpperCase() }}
            title={expense.product || expense.category}
            amount={fmtMoney(expense.total_cost, currency)}
            status={expense.authorizer ? { label: expense.authorizer, tone: undefined } : undefined}
            subtitle={subtitle}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: C.muted }}>{t('authorizer') || 'Authorizer'}</span>
                    <span style={{ fontWeight: 600, color: C.text }}>{expense.authorizer || '—'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: C.muted }}>{t('qty') || 'Qty'}</span>
                    <span style={{ fontWeight: 600, color: C.text }}>{expense.quantity}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: C.muted }}>{t('unit') || 'Unit'}</span>
                    <span style={{ fontWeight: 600, color: C.text }}>{fmtMoney(expense.price_per_unit, currency)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: C.muted }}>{t('total') || 'Total'}</span>
                    <span style={{ fontWeight: 800, color: C.text }}>{fmtMoney(expense.total_cost, currency)}</span>
                </div>
                {expense.inventory_id && (
                    <div style={{ fontSize: 12, color: C.green, fontWeight: 600 }}>
                        {t('linked_to_inventory_short') || 'Linked to inventory'}
                    </div>
                )}
                {expense.miscellaneous && (
                    <div style={{ fontSize: 12, color: C.muted, paddingTop: 6, borderTop: `1px solid #f1f5f9` }}>
                        {expense.miscellaneous}
                    </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button onClick={(e) => { e.stopPropagation(); onEdit(expense); }}
                        style={{ flex: 1, padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.green, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>
                        {t('edit') || 'Edit'}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(expense); }}
                        style={{ flex: 1, padding: '10px', borderRadius: 8, border: `1px solid #fecaca`, background: C.redLight, color: C.red, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>
                        {t('delete') || 'Delete'}
                    </button>
                </div>
            </div>
        </MobileCard>
    );
};

// ─── Expense Form (shared for both daily and overhead) ────────────────────────
const INVENTORY_CATEGORIES = ['Inventory', 'Inventory Purchase', 'المخزون'];

const UNIT_OPTIONS = ['grams', 'ml', 'pieces', 'kg', 'liters', 'cups', 'tablespoons', 'teaspoons'];

const ExpenseForm = ({ formData, onChange, categories, authorizers, currency, isOverhead, onSubmit, onCancel, submitting, isEdit, activeSessions, selectedSessionId, onSessionChange, eodMode, t, businessDay, inventoryItems, onInventorySelect, onRefreshInventory }) => {
    const totalAuto = (parseFloat(formData.price_per_unit) || 0) * (parseFloat(formData.quantity) || 0);
    const isInventoryCategory = INVENTORY_CATEGORIES.includes(formData.category);
    const selectedInvItem = isInventoryCategory && formData.inventory_id
        ? inventoryItems.find(i => i.inventory_id === formData.inventory_id)
        : null;
    const [creatingIngredient, setCreatingIngredient] = useState(false);
    const [newIngredient, setNewIngredient] = useState({ name: '', unit_of_measurement: '', purchase_unit: '', purchase_unit_size: '', supplier: '', minimum_stock_level: '' });
    const [creatingSaving, setCreatingSaving] = useState(false);

    return (
        <form onSubmit={onSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {isOverhead && (
                    <div>
                        <Label>{t('date')}</Label>
                        <Input type="date" name="date" value={formData.date} onChange={onChange} required />
                    </div>
                )}
                <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                        <Label>{t('authorizer')}</Label>
                        <Select name="authorizer" value={formData.authorizer} onChange={onChange} required>
                            <option value="">{t('select_authorizer')}</option>
                            {authorizers.map(e => (
                                <option key={e.id || e.employee_id} value={e.name}>{e.name}</option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <Label>{t('category')}</Label>
                        <Select name="category" value={formData.category} onChange={onChange} required>
                            <option value="">{t('select_category')}</option>
                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        </Select>
                    </div>
                </div>
                {!isOverhead && (
                    <div>
                        <Label>{t('sales_date') || 'Sales Date'}</Label>
                        <div style={{
                            padding: '8px 12px',
                            borderRadius: 6,
                            fontSize: 13,
                            fontFamily: FONT,
                            background: businessDay?.salesDate ? C.bg : C.redLight,
                            border: `1px solid ${businessDay?.salesDate ? C.border : '#fecaca'}`,
                            color: businessDay?.salesDate ? '#475569' : C.red,
                        }}>
                            {businessDay?.salesDate
                                ? (() => {
                                    try {
                                        const d = new Date((businessDay.salesDate || '').split(' ')[0] + 'T00:00:00');
                                        return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
                                    } catch { return businessDay.salesDate; }
                                })()
                                : (t('no_active_business_day') || 'No Active Business Day')
                            }
                        </div>
                    </div>
                )}
                {isInventoryCategory && inventoryItems.length > 0 ? (
                    <div>
                        <Label>{t('select_inventory_item') || 'Select Inventory Item'}</Label>
                        <Select
                            value={formData.inventory_id || ''}
                            onChange={(e) => {
                                const invId = e.target.value;
                                if (invId === '__add_new__') {
                                    setCreatingIngredient(true);
                                    onInventorySelect(null);
                                    return;
                                } else if (invId === '__manual__') {
                                    setCreatingIngredient(false);
                                    onInventorySelect(null);
                                } else if (invId) {
                                    setCreatingIngredient(false);
                                    const item = inventoryItems.find(i => i.inventory_id === invId);
                                    onInventorySelect(item);
                                } else {
                                    onInventorySelect(null);
                                }
                            }}
                        >
                            <option value="">{t('select_inventory_item') || 'Select Inventory Item'}</option>
                            {inventoryItems.map(item => (
                                <option key={item.inventory_id} value={item.inventory_id}>
                                    {item.name} ({item.unit_of_measurement})
                                </option>
                            ))}
                            <option value="__manual__">{t('or_enter_manually') || 'Or enter product name manually'}</option>
                            <option value="__add_new__" style={{ color: C.green }}>+ {t('add_new_ingredient') || 'Add New Ingredient'}</option>
                        </Select>
                        {creatingIngredient && (
                            <div style={{ marginTop: 8, padding: 14, border: `1.5px solid ${C.green}`, borderRadius: 8, background: C.bg }}>
                                <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: C.green }}>+ {t('add_ingredient')}</p>
                                <p style={{ margin: '0 0 12px', fontSize: 11, color: C.muted }}>{t('ingredient_instructions')}</p>
                                <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                    <div>
                                        <Label required>{t('name')}</Label>
                                        <Input value={newIngredient.name} onChange={e => setNewIngredient(p => ({ ...p, name: e.target.value }))} placeholder={t('placeholder_ingredient_name')} />
                                    </div>
                                    <div>
                                        <Label required>{t('unit_of_measurement')}</Label>
                                        <Select value={newIngredient.unit_of_measurement} onChange={e => setNewIngredient(p => ({ ...p, unit_of_measurement: e.target.value }))}>
                                            <option value="">{t('select_unit')}</option>
                                            {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>{t('purchase_unit')}</Label>
                                        <Input value={newIngredient.purchase_unit} onChange={e => setNewIngredient(p => ({ ...p, purchase_unit: e.target.value }))} placeholder={t('placeholder_purchase_unit')} />
                                    </div>
                                    <div>
                                        <Label>{newIngredient.unit_of_measurement && newIngredient.purchase_unit ? `${newIngredient.unit_of_measurement} per ${newIngredient.purchase_unit}` : t('purchase_unit_size')}</Label>
                                        <Input type="number" value={newIngredient.purchase_unit_size} onChange={e => setNewIngredient(p => ({ ...p, purchase_unit_size: e.target.value }))} placeholder={t('placeholder_unit_size')} min="0" step="0.1" />
                                    </div>
                                    <div>
                                        <Label>{t('supplier')}</Label>
                                        <Input value={newIngredient.supplier || ''} onChange={e => setNewIngredient(p => ({ ...p, supplier: e.target.value }))} placeholder={t('optional')} />
                                    </div>
                                    <div>
                                        <Label>{t('minimum_stock_level')}</Label>
                                        <Input type="number" value={newIngredient.minimum_stock_level || ''} onChange={e => setNewIngredient(p => ({ ...p, minimum_stock_level: e.target.value }))} placeholder={t('optional')} min="0" />
                                    </div>
                                </div>
                                {newIngredient.purchase_unit && newIngredient.purchase_unit_size && (
                                    <p style={{ margin: '6px 0 0', fontSize: 11, color: C.muted }}>1 {newIngredient.purchase_unit} = {newIngredient.purchase_unit_size} {newIngredient.unit_of_measurement}</p>
                                )}
                                <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                                    <button type="button" onClick={() => setCreatingIngredient(false)} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.white, fontSize: 12, cursor: 'pointer', color: C.muted }}>{t('cancel')}</button>
                                    <button type="button" disabled={creatingSaving || !newIngredient.name.trim() || !newIngredient.unit_of_measurement} onClick={async () => {
                                        setCreatingSaving(true);
                                        try {
                                            const created = await inventoryService.createInventoryItem({
                                                name: newIngredient.name.trim(),
                                                unit_of_measurement: newIngredient.unit_of_measurement,
                                                cost_per_unit: 0,
                                                current_stock: 0,
                                                purchase_unit: newIngredient.purchase_unit?.trim() || null,
                                                purchase_unit_size: newIngredient.purchase_unit_size ? parseFloat(newIngredient.purchase_unit_size) : null,
                                                minimum_stock_level: newIngredient.minimum_stock_level ? parseFloat(newIngredient.minimum_stock_level) : null,
                                                supplier: newIngredient.supplier?.trim() || null,
                                            });
                                            if (onRefreshInventory) await onRefreshInventory();
                                            onInventorySelect(created);
                                            setCreatingIngredient(false);
                                            setNewIngredient({ name: '', unit_of_measurement: '', purchase_unit: '', purchase_unit_size: '', supplier: '', minimum_stock_level: '' });
                                        } catch (e) { /* handle */ }
                                        setCreatingSaving(false);
                                    }} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: C.green, color: C.white, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: creatingSaving || !newIngredient.name.trim() || !newIngredient.unit_of_measurement ? 0.5 : 1 }}>
                                        {creatingSaving ? t('creating') || 'Creating...' : t('create_ingredient') || 'Create Ingredient'}
                                    </button>
                                </div>
                            </div>
                        )}
                        {selectedInvItem && (
                            <div style={{
                                marginTop: 6, padding: '6px 10px', borderRadius: 6,
                                background: C.greenLight, border: `1px solid #bfdbfe`,
                                fontSize: 12, color: C.greenDark, fontFamily: FONT,
                            }}>
                                <div style={{ fontWeight: 600 }}>{t('current_stock_label') || 'Current stock'}: {selectedInvItem.current_stock} {selectedInvItem.unit_of_measurement}</div>
                                <div style={{ fontSize: 11, color: C.green, marginTop: 2 }}>{t('linked_to_inventory') || 'Linked to inventory - stock will be updated automatically'}</div>
                            </div>
                        )}
                        {(!formData.inventory_id || formData.inventory_id === '') && (
                            <div style={{ marginTop: 6 }}>
                                <Label>{t('description')}</Label>
                                <Input type="text" name="product" value={formData.product} onChange={onChange} placeholder={t('expense_product_placeholder')} required />
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <Label>{t('description')}</Label>
                        <Input type="text" name="product" value={formData.product} onChange={onChange} placeholder={t('expense_product_placeholder')} required />
                    </div>
                )}
                {selectedInvItem?.purchase_unit && selectedInvItem?.purchase_unit_size > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                                <Label required>{t('quantity')} ({selectedInvItem.purchase_unit})</Label>
                                <Input type="number" name="quantity" value={formData.quantity} onChange={e => {
                                    const qty = e.target.value;
                                    const total = parseFloat(formData.total_cost_manual) || 0;
                                    const ppu = qty > 0 && total > 0 ? (total / parseFloat(qty)).toFixed(2) : formData.price_per_unit;
                                    onChange({ target: { name: 'quantity', value: qty } });
                                    if (total > 0) onChange({ target: { name: 'price_per_unit', value: ppu } });
                                }} min="1" step="1" required />
                            </div>
                            <div>
                                <Label required>{t('total_paid')} ({currency})</Label>
                                <Input type="number" name="total_cost_manual" value={formData.total_cost_manual || ''} onChange={e => {
                                    const total = e.target.value;
                                    const qty = parseFloat(formData.quantity) || 0;
                                    const ppu = qty > 0 && total > 0 ? (parseFloat(total) / qty).toFixed(2) : '0';
                                    onChange({ target: { name: 'total_cost_manual', value: total } });
                                    onChange({ target: { name: 'price_per_unit', value: ppu } });
                                }} min="0" step="0.01" placeholder={t('placeholder_amount')} required />
                            </div>
                        </div>
                        {parseFloat(formData.quantity) > 0 && parseFloat(formData.total_cost_manual) > 0 && (
                            <div style={{ background: C.greenLight, border: `1px solid #bfdbfe`, borderRadius: 6, padding: '8px 12px', fontSize: 12, color: C.greenDark }}>
                                <div><strong>{parseFloat(formData.quantity)} {selectedInvItem.purchase_unit}</strong> × {currency}{(parseFloat(formData.total_cost_manual) / parseFloat(formData.quantity)).toFixed(2)} {t('per_unit')} = <strong>{currency}{parseFloat(formData.total_cost_manual).toFixed(2)}</strong></div>
                                <div style={{ marginTop: 2, fontSize: 11, color: C.green }}>= {(parseFloat(formData.quantity) * parseFloat(selectedInvItem.purchase_unit_size)).toFixed(1)} {selectedInvItem.unit_of_measurement} {t('will_be_added')}</div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="portal-grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                        <div>
                            <Label>{t('price_per_unit')} ({currency})</Label>
                            <Input type="number" name="price_per_unit" value={formData.price_per_unit} onChange={onChange} min="0" step="0.01" required />
                        </div>
                        <div>
                            <Label>{t('quantity')}</Label>
                            <Input type="number" name="quantity" value={formData.quantity} onChange={onChange} min="1" step="1" required />
                        </div>
                        <div>
                            <Label>{t('total')} ({currency})</Label>
                            <Input type="text" value={totalAuto.toFixed(2)} readOnly style={{ background: C.bg, color: C.green, fontWeight: 700 }} />
                        </div>
                    </div>
                )}
                <div>
                    <Label>{t('notes')} ({t('optional')})</Label>
                    <Textarea name="miscellaneous" value={formData.miscellaneous} onChange={onChange} rows={2} />
                </div>
                {!isOverhead && (
                    <>
                        {eodMode === 'per_till' ? (
                            activeSessions && activeSessions.length > 0 ? (
                                <div>
                                    <Label>{t('select_till')} *</Label>
                                    <Select value={selectedSessionId || ''} onChange={e => onSessionChange(e.target.value)} required>
                                        <option value="" disabled>— {t('select_till')} —</option>
                                        {activeSessions.map(s => (
                                            <option key={s.session_id} value={s.session_id}>
                                                {s.employee_name} — {s.session_id}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            ) : (
                                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#dc2626' }}>
                                    {t('no_active_sessions')}
                                </div>
                            )
                        ) : (
                            activeSessions && activeSessions.length > 0 ? (
                                <div style={{ background: '#eff5ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#2563eb' }}>
                                    {t('expense_logged_to_active_session')}
                                </div>
                            ) : (
                                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#dc2626' }}>
                                    {t('no_active_session_start_day')}
                                </div>
                            )
                        )}
                    </>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 }}>
                    <Btn type="button" variant="secondary" onClick={onCancel}>{t('cancel')}</Btn>
                    <Btn type="submit" disabled={submitting}>{submitting ? t('saving') : (isEdit ? t('update') : t('add'))}</Btn>
                </div>
                {!isOverhead && !businessDay?.salesDate && (
                    <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#92400e', marginTop: 4, textAlign: 'center' }}>
                        {t('expense_will_be_overhead') || 'No active business day — this expense will be saved as an overhead expense'}
                    </div>
                )}
            </div>
        </form>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────
const PortalExpensesPageInner = () => {
    const { t } = useLanguage();
    const { getCurrency, getTenantSetting } = useAuth();
    const currency = getCurrency();
    const isMobile = useIsMobile();

    const dailyCategories    = getTenantSetting('daily_expense_categories',    DEFAULT_DAILY_CATEGORIES);
    const overheadCategories = getTenantSetting('overhead_expense_categories', DEFAULT_OVERHEAD_CATEGORIES);
    const eodMode = getTenantSetting('eod_mode', 'per_till');

    const [tab, setTab]               = useState('daily');   // 'daily' | 'overheads'
    const [dailyExpenses, setDailyExpenses]       = useState([]);
    const [overheads, setOverheads]               = useState([]);
    const [authorizers, setAuthorizers]           = useState([]);
    const [businessDay, setBusinessDay]           = useState({ active: false, salesDate: '' });
    const [loading, setLoading]                   = useState(true);
    const [toasts, setToasts]                     = useState([]);

    // Modal state
    const [modalOpen, setModalOpen]               = useState(false);
    const [modalType, setModalType]               = useState('daily'); // 'daily' | 'overhead'
    const [editTarget, setEditTarget]             = useState(null);
    const [submitting, setSubmitting]             = useState(false);
    const [formData, setFormData]                 = useState(emptyForm('daily'));

    // Scan receipt state
    const [scanModal, setScanModal]               = useState(false);
    const [processing, setProcessing]             = useState(false);
    const [scannedItems, setScannedItems]         = useState([]);
    const [reviewModal, setReviewModal]           = useState(false);
    const [scanAuthorizer, setScanAuthorizer]     = useState('');

    // EOD session linking
    const [activeSessions, setActiveSessions]     = useState([]);
    const [selectedSessionId, setSelectedSessionId] = useState('');

    // Inventory items for linking
    const [inventoryItems, setInventoryItems]     = useState([]);

    const deletionConfirm = useDeletionConfirm();

    // ── Toast helpers ──────────────────────────────────────────────────────────
    const toast = useCallback((message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
    }, []);

    // ── Empty form factory ─────────────────────────────────────────────────────
    function emptyForm(type) {
        return {
            date:           format(new Date(), 'yyyy-MM-dd'),
            time:           format(new Date(), 'HH:mm:ss'),
            authorizer:     '',
            category:       '',
            product:        '',
            price_per_unit: '',
            quantity:       1,
            miscellaneous:  '',
            inventory_id:   '',
            total_cost_manual: '',
        };
    }

    // ── Load initial data ──────────────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                // Business day state
                const state = await tenantStorage.getBusinessDayState();
                const isActive   = state.isBusinessDay || false;
                const salesDate  = state.salesDate || '';
                setBusinessDay({ active: isActive, salesDate });

                // Authorizers (active cashiers)
                const employees = await employeeService.getAllEmployees();
                const cashiers  = employees.filter(e => e.status?.toLowerCase() === 'active' && !!e.is_cashier);
                setAuthorizers(cashiers);

                // Expenses
                if (isActive && salesDate) {
                    const raw = await expenseService.getExpensesAfterStartOfDay(salesDate);
                    setDailyExpenses(raw.filter(e => e.expense_type === 'daily' || !e.expense_type));
                }
                const oh = await expenseService.getOverheads(30);
                setOverheads(oh);

                // Fetch inventory items for linking
                try {
                    const inv = await inventoryService.getAllInventory();
                    setInventoryItems(inv || []);
                } catch (invErr) {
                    console.warn('Failed to load inventory items:', invErr);
                }
            } catch (err) {
                console.error('PortalExpensesPage init error:', err);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, []);

    // ── Load active sessions for EOD linking ────────────────────────────────────
    useEffect(() => {
        const loadSessions = async () => {
            try {
                const sessions = await sessionService.getActiveSessions();
                setActiveSessions(sessions);
            } catch (err) {
                console.error('Failed to load sessions:', err);
            }
        };
        loadSessions();
    }, [eodMode]);

    // ── Refresh helpers ────────────────────────────────────────────────────────
    const refreshDaily = async () => {
        if (!businessDay.active || !businessDay.salesDate) { setDailyExpenses([]); return; }
        const raw = await expenseService.getExpensesAfterStartOfDay(businessDay.salesDate);
        setDailyExpenses(raw.filter(e => e.expense_type === 'daily' || !e.expense_type));
    };

    const refreshOverheads = async () => {
        const oh = await expenseService.getOverheads(30);
        setOverheads(oh);
    };

    // ── Open add/edit modals ──────────────────────────────────────────────────
    const openAdd = (type) => {
        setEditTarget(null);
        setModalType(type);
        const defaults = type === 'daily'
            ? { ...emptyForm('daily'), authorizer: authorizers[0]?.name || '', category: dailyCategories[0] || '' }
            : { ...emptyForm('overhead'), authorizer: authorizers[0]?.name || '', category: overheadCategories[0] || '' };
        setFormData(defaults);
        setModalOpen(true);
    };

    const openEdit = (expense) => {
        const type = expense.expense_type === 'overhead' ? 'overhead' : 'daily';
        setEditTarget(expense);
        setModalType(type);
        setFormData({
            date:           expense.date,
            time:           expense.time,
            authorizer:     expense.authorizer,
            category:       expense.category,
            product:        expense.product,
            price_per_unit: expense.price_per_unit,
            quantity:       expense.quantity,
            miscellaneous:  expense.miscellaneous || '',
            inventory_id:   expense.inventory_id || '',
            total_cost_manual: expense.total_cost ? String(expense.total_cost) : '',
        });
        setModalOpen(true);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const next = { ...prev, [name]: value };
            if (name === 'price_per_unit' || name === 'quantity') {
                const p = name === 'price_per_unit' ? parseFloat(value) || 0 : parseFloat(prev.price_per_unit) || 0;
                const q = name === 'quantity'       ? parseFloat(value) || 0 : parseFloat(prev.quantity) || 0;
                next.total_cost = (p * q).toFixed(2);
            }
            // Clear inventory link when category changes away from inventory
            if (name === 'category' && !['Inventory', 'Inventory Purchase', 'المخزون'].includes(value)) {
                next.inventory_id = '';
            }
            return next;
        });
    };

    const handleInventorySelect = (item) => {
        if (item) {
            setFormData(prev => ({
                ...prev,
                inventory_id: item.inventory_id,
                product: item.name,
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                inventory_id: '',
                product: '',
            }));
        }
    };

    // ── Submit ─────────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const noBusinessDay = !businessDay?.salesDate;
            const isOverhead = modalType === 'overhead' || (noBusinessDay && modalType === 'daily');

            // For per_till mode, validate that a session is selected
            if (!isOverhead && eodMode === 'per_till' && !selectedSessionId) {
                toast(t('please_select_till_session'), 'error');
                setSubmitting(false);
                return;
            }

            // For per_store mode, auto-link to first active session
            const sessionId = isOverhead ? null
                : eodMode === 'per_till' ? selectedSessionId
                : (activeSessions && activeSessions.length > 0 ? activeSessions[0].session_id : null);

            const payload = {
                ...formData,
                price_per_unit: parseFloat(formData.price_per_unit),
                quantity:       parseInt(formData.quantity, 10),
                total_cost:     formData.total_cost_manual ? parseFloat(formData.total_cost_manual) : parseFloat(formData.price_per_unit) * parseInt(formData.quantity, 10),
                expense_type:   isOverhead ? 'overhead' : 'daily',
                sales_date:     isOverhead ? null : (businessDay.salesDate || null),
                time:           isOverhead ? format(new Date(), 'HH:mm:ss') : format(new Date(), 'HH:mm:ss'),
                date:           isOverhead ? formData.date : format(new Date(), 'yyyy-MM-dd'),
                inventory_id:   formData.inventory_id || null,
                ...(sessionId ? { session_id: sessionId } : {}),
            };

            if (editTarget) {
                await expenseService.updateExpense(editTarget.expense_id, payload);
                toast(isOverhead ? t('overhead_updated') : t('expense_updated'));
            } else {
                await expenseService.createExpense(payload);
                toast(isOverhead ? t('overhead_added') : t('expense_added'));
            }

            setModalOpen(false);
            if (isOverhead) await refreshOverheads();
            else await refreshDaily();
        } catch (err) {
            console.error('Submit expense error:', err);
            toast(t('failed_to_save_expense'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Delete ─────────────────────────────────────────────────────────────────
    const handleDelete = (expense) => {
        const isOverhead = expense.expense_type === 'overhead';
        deletionConfirm.confirmDeletion({
            title:       isOverhead ? t('delete_overhead') : t('delete_expense'),
            message:     t('confirm_delete_expense') || 'Are you sure you want to delete this expense?',
            itemName:    `${expense.category} — ${expense.product} (${currency}${parseFloat(expense.total_cost).toFixed(2)})`,
            requireReason: true,
            onDelete: async (reason) => {
                await expenseService.deleteExpense(expense.expense_id, reason);
                if (isOverhead) await refreshOverheads();
                else await refreshDaily();
                toast(t('expense_deleted'));
            },
        });
    };

    // ── Receipt scanning ───────────────────────────────────────────────────────
    const handleFileCapture = async (file) => {
        setScanModal(false);
        setProcessing(true);
        try {
            const fd = new FormData();
            fd.append('receipt_image', file);
            const response = await expenseService.scanReceipt(fd);
            if (response.items?.length > 0) {
                setScannedItems(response.items);
                setScanAuthorizer(authorizers[0]?.name || '');
                setReviewModal(true);
            } else {
                toast(t('no_items_found_in_receipt'), 'error');
            }
        } catch (err) {
            toast(t('receipt_scanning_failed'), 'error');
        } finally {
            setProcessing(false);
        }
    };

    const handleBatchCreate = async (items) => {
        try {
            for (const item of items) {
                await expenseService.createExpense({
                    date:           format(new Date(), 'yyyy-MM-dd'),
                    time:           format(new Date(), 'HH:mm:ss'),
                    authorizer:     scanAuthorizer || (authorizers[0]?.name || ''),
                    category:       item.category || 'Other',
                    product:        item.product_name,
                    price_per_unit: item.unit_price,
                    quantity:       item.quantity,
                    total_cost:     item.total_cost,
                    miscellaneous:  item.notes || t('scanned_from_receipt'),
                    expense_type:   'daily',
                    sales_date:     businessDay.salesDate || null,
                });
            }
            toast(t('added_expenses_from_receipt', { count: items.length }));
            setReviewModal(false);
            await refreshDaily();
        } catch (err) {
            toast(t('failed_to_add_expenses'), 'error');
        }
    };

    // ── Summary data ───────────────────────────────────────────────────────────
    const dailyTotal = dailyExpenses.reduce((s, e) => s + (parseFloat(e.total_cost) || 0), 0);
    const overheadTotal = overheads.reduce((s, e) => s + (parseFloat(e.total_cost) || 0), 0);

    const dailyCategoryTotals = dailyExpenses.reduce((acc, e) => {
        acc[e.category] = (acc[e.category] || 0) + (parseFloat(e.total_cost) || 0);
        return acc;
    }, {});

    // ── Tab bar ────────────────────────────────────────────────────────────────
    const tabs = [
        { key: 'daily',    label: t('daily')    || 'Daily'    },
        { key: 'overheads', label: t('overheads') || 'Overheads' },
    ];

    // ── Table columns ──────────────────────────────────────────────────────────
    const dailyCols = [
        {
            key: 'product', label: t('description_product') || 'Description / Product', width: '1fr',
            render: (r) => (
                <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: FONT }}>{r.product}</div>
                    <div style={{ fontSize: 11, color: C.mutedLight, fontFamily: FONT }}>{r.time?.slice(0, 5)}</div>
                </div>
            ),
        },
        {
            key: 'category', label: t('category'), width: '100px',
            render: (r) => <Badge {...catColour(r.category)}>{r.category}</Badge>,
        },
        {
            key: 'authorizer', label: t('authorizer'), width: '110px',
            render: (r) => <span style={{ fontSize: 12, color: C.muted, fontFamily: FONT }}>{r.authorizer}</span>,
        },
        { key: 'quantity',      label: t('qty'),   width: '50px',  render: (r) => <span style={{ fontSize: 13, color: C.text, fontFamily: FONT }}>{r.quantity}</span> },
        { key: 'price_per_unit', label: t('unit'),  width: '80px',  render: (r) => <span style={{ fontSize: 13, color: C.text, fontFamily: FONT }}>{currency}{parseFloat(r.price_per_unit).toFixed(2)}</span> },
        { key: 'total_cost',    label: t('total'),  width: '90px',  render: (r) => <span style={{ fontSize: 13, fontWeight: 800, color: C.text, fontFamily: FONT }}>{currency}{parseFloat(r.total_cost).toFixed(2)}</span> },
        {
            key: 'actions', label: '', width: '80px',
            render: (r) => (
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={(e) => { e.stopPropagation(); openEdit(r); }}
                        style={{ fontSize: 11, fontWeight: 600, color: C.green, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT }}>
                        {t('edit')}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(r); }}
                        style={{ fontSize: 11, fontWeight: 600, color: C.red, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT }}>
                        {t('delete')}
                    </button>
                </div>
            ),
        },
    ];

    const overheadCols = [
        {
            key: 'date', label: t('date'), width: '90px',
            render: (r) => <span style={{ fontSize: 12, color: C.muted, fontFamily: FONT }}>{r.date}</span>,
        },
        {
            key: 'product', label: t('description'), width: '1fr',
            render: (r) => <span style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: FONT }}>{r.product}</span>,
        },
        {
            key: 'category', label: t('category'), width: '100px',
            render: (r) => <Badge {...catColour(r.category)}>{r.category}</Badge>,
        },
        {
            key: 'authorizer', label: t('authorizer'), width: '120px',
            render: (r) => <span style={{ fontSize: 12, color: C.muted, fontFamily: FONT }}>{r.authorizer}</span>,
        },
        { key: 'total_cost', label: t('total'), width: '100px', render: (r) => <span style={{ fontSize: 13, fontWeight: 800, color: C.text, fontFamily: FONT }}>{currency}{parseFloat(r.total_cost).toFixed(2)}</span> },
        {
            key: 'actions', label: '', width: '80px',
            render: (r) => (
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={(e) => { e.stopPropagation(); openEdit(r); }}
                        style={{ fontSize: 11, fontWeight: 600, color: C.green, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT }}>
                        {t('edit')}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(r); }}
                        style={{ fontSize: 11, fontWeight: 600, color: C.red, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT }}>
                        {t('delete')}
                    </button>
                </div>
            ),
        },
    ];

    // ── Render ─────────────────────────────────────────────────────────────────
    const modalCategories = modalType === 'overhead' ? overheadCategories : dailyCategories;
    const modalTitle = editTarget
        ? (modalType === 'overhead' ? (t('edit_overhead') || 'Edit Overhead') : (t('edit_daily_expense') || 'Edit Daily Expense'))
        : (modalType === 'overhead' ? (t('add_overhead') || 'Add Overhead') : (t('add_daily_expense') || 'Add Daily Expense'));

    return (
        <>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap'); @keyframes fadeIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}`}</style>
            <ToastContainer toasts={toasts} />

            <div style={{ fontFamily: FONT }}>
                {/* Page header */}
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', gap: isMobile ? 14 : 0, marginBottom: 20 }}>
                    <div>
                        <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0, fontFamily: FONT }}>{t('expenses') || 'Expenses'}</h1>
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 3, fontFamily: FONT }}>
                            {tab === 'daily'
                                ? (businessDay.active ? `${t('business_day') || 'Business Day'} · ${businessDay.salesDate}` : (t('no_active_business_day') || 'No active business day'))
                                : `${t('last_30_days') || 'Last 30 days'}`}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, width: isMobile ? '100%' : 'auto' }}>
                        {tab === 'daily' && (
                            <Btn variant="secondary" onClick={() => setScanModal(true)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flex: isMobile ? 1 : undefined }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                                {t('scan_receipt') || 'Scan Receipt'}
                            </Btn>
                        )}
                        <Btn onClick={() => openAdd(tab === 'daily' ? 'daily' : 'overhead')} style={{ flex: isMobile ? 1 : undefined, justifyContent: 'center' }}>
                            + {tab === 'daily' ? (t('add_daily_expense') || 'Add Daily Expense') : (t('add_overhead') || 'Add Overhead')}
                        </Btn>
                    </div>
                </div>

                {/* Tab bar */}
                <div style={{ display: 'flex', borderBottom: `2px solid ${C.border}`, marginBottom: 20 }}>
                    {tabs.map(tb => (
                        <button key={tb.key} onClick={() => setTab(tb.key)} style={{
                            padding: '10px 20px', fontSize: 13, fontWeight: 700, fontFamily: FONT,
                            color: tab === tb.key ? C.green : C.mutedLight,
                            borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                            borderBottom: `2px solid ${tab === tb.key ? C.green : 'transparent'}`,
                            marginBottom: -2, background: 'none',
                            cursor: 'pointer', transition: 'color 0.15s',
                        }}>
                            {tb.label}
                        </button>
                    ))}
                </div>

                {/* ── DAILY TAB ─────────────────────────────────────────────────── */}
                {tab === 'daily' && (
                    <div>
                        {/* Summary strip */}
                        {isMobile ? (
                            businessDay.active && dailyExpenses.length > 0 && (
                                <MobileHero
                                    label={t('today_total') || "Today's Total"}
                                    value={`${currency}${dailyTotal.toFixed(2)}`}
                                    meta={`${dailyExpenses.length} ${dailyExpenses.length !== 1 ? (t('expenses') || 'expenses') : (t('expense') || 'expense')} · ${businessDay.salesDate || ''}`}
                                    stats={Object.entries(dailyCategoryTotals).slice(0, 3).map(([cat, amt]) => ({
                                        label: cat,
                                        value: `${currency}${amt.toFixed(2)}`,
                                    }))}
                                />
                            )
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${1 + Object.keys(dailyCategoryTotals).length}, 1fr)`, gap: 10, marginBottom: 16 }}>
                                <SummaryCard label={t('today_total') || "Today's Total"} value={`${currency}${dailyTotal.toFixed(2)}`} highlight />
                                {Object.entries(dailyCategoryTotals).map(([cat, amt]) => (
                                    <SummaryCard key={cat} label={cat} value={`${currency}${amt.toFixed(2)}`} />
                                ))}
                            </div>
                        )}

                        {/* No business day */}
                        {!businessDay.active ? (
                            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                                <EmptyState
                                    icon="📅"
                                    message={t('no_active_business_day_portal') || 'No active business day'}
                                    sub={t('start_business_day_to_log') || 'Start a business day to log daily expenses.'}
                                />
                            </div>
                        ) : loading ? (
                            <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontFamily: FONT }}>{t('loading')}</div>
                        ) : dailyExpenses.length === 0 ? (
                            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                                <EmptyState icon="🧾" message={t('no_daily_expenses') || 'No expenses logged today'} sub={t('tap_add_daily_expense') || "Tap 'Add Daily Expense' to record one."} />
                            </div>
                        ) : isMobile ? (
                            <div>
                                {dailyExpenses.map(e => (
                                    <MobileExpenseRow key={e.expense_id} expense={e} currency={currency} isOverhead={false} onEdit={openEdit} onDelete={handleDelete} t={t} />
                                ))}
                            </div>
                        ) : (
                            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                                <TableHeader cols={dailyCols} />
                                {dailyExpenses.map(e => (
                                    <TableRow key={e.expense_id} cols={dailyCols} row={e} onClick={() => openEdit(e)} />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* ── OVERHEADS TAB ─────────────────────────────────────────────── */}
                {tab === 'overheads' && (
                    <div>
                        {/* Info strip */}
                        {isMobile ? (
                            overheads.length > 0 && (
                                <MobileHero
                                    label={t('last_30_days') || 'Last 30 days'}
                                    value={`${currency}${overheadTotal.toFixed(2)}`}
                                    meta={`${overheads.length} ${overheads.length !== 1 ? (t('overheads') || 'overheads') : (t('overhead') || 'overhead')}`}
                                />
                            )
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: C.greenLight, borderRadius: 9, marginBottom: 16, border: `1px solid #b7dfc8` }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: C.green, fontFamily: FONT }}>{t('last_30_days') || 'Last 30 days'}</span>
                                <span style={{ fontSize: 12, color: C.muted }}>·</span>
                                <span style={{ fontSize: 14, fontWeight: 800, color: C.greenDark, fontFamily: FONT }}>{currency}{overheadTotal.toFixed(2)} {t('total') || 'total'}</span>
                            </div>
                        )}

                        {loading ? (
                            <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontFamily: FONT }}>{t('loading')}</div>
                        ) : overheads.length === 0 ? (
                            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                                <EmptyState icon="🏢" message={t('no_overhead_expenses') || 'No overheads in the last 30 days'} sub={t('tap_add_overhead') || "Tap 'Add Overhead' to record rent, utilities, bills, etc."} />
                            </div>
                        ) : isMobile ? (
                            <div>
                                {overheads.map(e => (
                                    <MobileExpenseRow key={e.expense_id} expense={e} currency={currency} isOverhead onEdit={openEdit} onDelete={handleDelete} t={t} />
                                ))}
                            </div>
                        ) : (
                            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                                <TableHeader cols={overheadCols} />
                                {overheads.map(e => (
                                    <TableRow key={e.expense_id} cols={overheadCols} row={e} onClick={() => openEdit(e)} />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── Add / Edit modal ──────────────────────────────────────────────── */}
            <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={modalTitle}>
                <ExpenseForm
                    formData={formData}
                    onChange={handleFormChange}
                    categories={modalCategories}
                    authorizers={authorizers}
                    currency={currency}
                    isOverhead={modalType === 'overhead'}
                    onSubmit={handleSubmit}
                    onCancel={() => setModalOpen(false)}
                    submitting={submitting}
                    isEdit={!!editTarget}
                    activeSessions={activeSessions}
                    selectedSessionId={selectedSessionId}
                    onSessionChange={setSelectedSessionId}
                    eodMode={eodMode}
                    t={t}
                    businessDay={businessDay}
                    inventoryItems={inventoryItems}
                    onInventorySelect={handleInventorySelect}
                    onRefreshInventory={async () => { const inv = await inventoryService.getAllInventory(); setInventoryItems(inv); }}
                />
            </Modal>

            {/* ── Deletion confirm modal ─────────────────────────────────────────── */}
            <DeletionConfirmModal
                isOpen={deletionConfirm.isModalOpen}
                onClose={deletionConfirm.handleCancel}
                onConfirm={deletionConfirm.handleConfirm}
                loading={deletionConfirm.loading}
                {...deletionConfirm.modalProps}
            />

            {/* ── Scan receipt modal ────────────────────────────────────────────── */}
            <Modal open={scanModal} onClose={() => setScanModal(false)} title={t('scan_receipt') || 'Scan Receipt'}>
                <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: 13, color: C.muted, marginBottom: 24, fontFamily: FONT }}>
                        {t('scan_receipt_description') || 'Take a photo or upload an image to auto-extract expense items.'}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                        <div>
                            <input type="file" accept="image/*" capture="environment" id="cam-input"
                                style={{ display: 'none' }}
                                onChange={(e) => e.target.files[0] && handleFileCapture(e.target.files[0])} />
                            <label htmlFor="cam-input">
                                <Btn as="span" style={{ cursor: 'pointer' }}>📷 {t('take_photo') || 'Take Photo'}</Btn>
                            </label>
                        </div>
                        <span style={{ color: C.mutedLight, lineHeight: '36px', fontFamily: FONT }}>{t('or') || 'or'}</span>
                        <div>
                            <input type="file" accept="image/*" id="file-input"
                                style={{ display: 'none' }}
                                onChange={(e) => e.target.files[0] && handleFileCapture(e.target.files[0])} />
                            <label htmlFor="file-input">
                                <Btn as="span" variant="secondary" style={{ cursor: 'pointer' }}>📁 {t('choose_file') || 'Choose File'}</Btn>
                            </label>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
                        <Btn variant="secondary" onClick={() => setScanModal(false)}>{t('cancel')}</Btn>
                    </div>
                </div>
            </Modal>

            {/* ── Processing modal ──────────────────────────────────────────────── */}
            <Modal open={processing} onClose={() => {}} title={t('processing_receipt')} width={360}>
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid ${C.greenLight}`, borderTopColor: C.green, animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                    <p style={{ fontSize: 13, color: C.muted, fontFamily: FONT }}>{t('scanning_receipt')}</p>
                    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                </div>
            </Modal>

            {/* ── Review scanned items modal ────────────────────────────────────── */}
            <Modal open={reviewModal} onClose={() => setReviewModal(false)} title={t('review_scanned_items')} width={700}>
                <div>
                    <div style={{ padding: '8px 12px', background: C.greenLight, borderRadius: 7, marginBottom: 16, fontSize: 13, color: C.greenDark, fontWeight: 600, fontFamily: FONT }}>
                        {t('found_items_review', { count: scannedItems.length })}
                    </div>
                    <div style={{ marginBottom: 16 }}>
                        <Label>{t('authorizer')}</Label>
                        <Select value={scanAuthorizer} onChange={(e) => setScanAuthorizer(e.target.value)}>
                            <option value="">{t('select_authorizer')}</option>
                            {authorizers.map(e => <option key={e.id || e.employee_id} value={e.name}>{e.name}</option>)}
                        </Select>
                    </div>
                    <div className="portal-table-wrap" style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: FONT }}>
                            <thead>
                                <tr style={{ background: C.bg }}>
                                    {[t('include'), t('product_service'), t('category'), t('qty'), t('unit_price'), t('total')].map(h => (
                                        <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8 }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {scannedItems.map((item, i) => (
                                    <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                                        <td style={{ padding: '7px 10px' }}>
                                            <input type="checkbox" checked={item.included !== false} style={{ accentColor: C.green }}
                                                onChange={(e) => {
                                                    const next = [...scannedItems];
                                                    next[i].included = e.target.checked;
                                                    setScannedItems(next);
                                                }} />
                                        </td>
                                        <td style={{ padding: '7px 10px' }}>
                                            <Input value={item.product_name || ''} onChange={(e) => { const next = [...scannedItems]; next[i].product_name = e.target.value; setScannedItems(next); }} />
                                        </td>
                                        <td style={{ padding: '7px 10px' }}>
                                            <Select value={item.category || 'Other'} onChange={(e) => { const next = [...scannedItems]; next[i].category = e.target.value; setScannedItems(next); }}>
                                                {dailyCategories.map(c => <option key={c} value={c}>{c}</option>)}
                                            </Select>
                                        </td>
                                        <td style={{ padding: '7px 10px' }}>
                                            <Input type="number" min="1" style={{ width: 60 }} value={item.quantity || 1}
                                                onChange={(e) => { const next = [...scannedItems]; next[i].quantity = parseInt(e.target.value) || 1; next[i].total_cost = next[i].unit_price * next[i].quantity; setScannedItems(next); }} />
                                        </td>
                                        <td style={{ padding: '7px 10px' }}>
                                            <Input type="number" min="0" step="0.01" style={{ width: 80 }} value={item.unit_price || 0}
                                                onChange={(e) => { const next = [...scannedItems]; next[i].unit_price = parseFloat(e.target.value) || 0; next[i].total_cost = next[i].unit_price * next[i].quantity; setScannedItems(next); }} />
                                        </td>
                                        <td style={{ padding: '7px 10px', fontWeight: 700 }}>{currency}{(item.total_cost || 0).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                        <span style={{ fontSize: 12, color: C.muted, fontFamily: FONT }}>
                            {scannedItems.filter(i => i.included !== false).length} {t('items_selected')}
                        </span>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Btn variant="secondary" onClick={() => setReviewModal(false)}>{t('cancel')}</Btn>
                            <Btn onClick={() => handleBatchCreate(scannedItems.filter(i => i.included !== false))}>
                                {t('add')} {scannedItems.filter(i => i.included !== false).length} {t('expenses')}
                            </Btn>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

const PortalExpensesPage = () => (
    <PortalLayout>
        <PortalExpensesPageInner />
    </PortalLayout>
);

export default PortalExpensesPage;
